import { Drawer, Input, type Theme, useMediaQuery } from '@mui/material'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import { AppGreen } from 'appconstants'
import { ReactComponent as ArrowBack } from 'assets/arrow_back.svg'
import { ReactComponent as Close } from 'assets/close.svg'
import SVGMenu from 'assets/svgmenu'
import StepDrawer from 'components/Drawer/StepDrawer'
import { HeaderContext } from 'hooks/headerContext'
import { toolbarStyle } from 'pages/home/molecules/HomeHeader/index.style'
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Pages } from 'routes/types'
import { getRoutePath } from 'utils/routes.utils'
import { isMalaysia } from 'utils/tenant'
import { isEmpty } from 'utils/validation'

export const Header: React.FC = () => {
  const menuId = 'primary-search-account-menu'
  const { header } = useContext(HeaderContext)
  const [openDrawer, setDrawer] = useState(false)
  const [desktopDrawerOpen, setDesktopDrawerOpen] = useState(true)
  const [search, setSearch] = useState('')
  const navigate = useNavigate()
  const isMalaysiaHeader = isMalaysia()
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))
  let selectedOppId = localStorage.getItem('oppId')

  const setSearchResult = (e) => {
    setSearch(e.target.value)
    header?.searchResult(e.target.value)
  }

  useEffect(() => {
    setSearch('')
  }, [header?.showSearch])

  useEffect(() => {
    if (!isMalaysiaHeader || !header.showHomeDrawerIcon) {
      document.body.style.removeProperty('--mys-drawer-offset')
      return
    }

    document.body.style.setProperty('--mys-drawer-offset', desktopDrawerOpen ? '300px' : '0px')
  }, [desktopDrawerOpen, header.showHomeDrawerIcon, isMalaysiaHeader])

  return header.showHeader ? (
    <Box sx={{ flexGrow: 1, flexWrap: 'wrap' }}>
      <AppBar position='fixed' elevation={2} sx={{ m: 0 }}>
        <Toolbar sx={toolbarStyle}>
          {header.showHomeDrawerIcon && (
            <IconButton
              size='large'
              aria-label='Open menu'
              aria-controls={menuId}
              aria-haspopup='true'
              aria-expanded={isDesktop ? desktopDrawerOpen : openDrawer}
              onClick={() => {
                if (isMalaysiaHeader && isDesktop) {
                  setDesktopDrawerOpen((prev) => !prev)
                  return
                }
                setDrawer(true)
              }}
              color='inherit'
            >
              <SVGMenu />
            </IconButton>
          )}
          {header.showBackButton && (
            <IconButton
              size='large'
              aria-label='Back'
              aria-controls={menuId}
              aria-haspopup='true'
              onClick={() => {
                navigate(-1)
              }}
              color='inherit'
              sx={isMalaysiaHeader && header.showHomeDrawerIcon ? { display: 'none' } : undefined}
            >
              <ArrowBack />
            </IconButton>
          )}

          {header.showSearch ? (
            <>
              <Input
                name='headersearch'
                value={search}
                sx={{ flexGrow: 1 }}
                disableUnderline
                autoFocus
                onChange={setSearchResult}
                placeholder={header?.searchPlaceholder}
                autoComplete='off' // TODO: take in as a prop (if required)
              />
              {search !== '' && (
                <IconButton
                  size='large'
                  aria-label='Back'
                  aria-controls={menuId}
                  aria-haspopup='true'
                  onClick={() => {
                    setSearch('')
                    header?.searchResult('')
                  }}
                  color='inherit'
                >
                  <Close />
                </IconButton>
              )}
            </>
          ) : (
            <>
              <Typography
                variant='h1'
                id='header_title'
                noWrap
                component='div'
                sx={{
                  flexGrow: 1,
                  color: isMalaysiaHeader ? '#171D1B' : AppGreen,
                  fontFamily: isMalaysiaHeader ? '"Inter", "Roboto", sans-serif' : undefined,
                  fontSize: isMalaysiaHeader ? '16px' : '1rem',
                  fontWeight: isMalaysiaHeader ? 600 : undefined,
                  lineHeight: isMalaysiaHeader ? '24px' : undefined,
                  letterSpacing: isMalaysiaHeader ? '0.15px' : undefined,
                }}
              >
                {header.title}
              </Typography>
              <Box sx={{ display: 'flex' }}>{header.rightIcon}</Box>
            </>
          )}
        </Toolbar>
      </AppBar>

      {header.showHomeDrawerIcon === false ? (
        <Box></Box>
      ) : (
        <Box>
          <Drawer
            variant={'temporary'}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: isMalaysiaHeader ? { xs: 'min(360px, 100vw)', md: '300px' } : '300px',
                background: { xs: '#fff', md: '#fdfdfd' },
                mt: {
                  xs: '0px',
                  md: '65px',
                },
              },
              display: { xs: 'block', md: 'none' },
            }}
            anchor={'left'}
            open={openDrawer}
            onClose={() => {
              setDrawer(false)
            }}
          >
            <Box sx={{ overflowY: 'auto', height: '100%', pb: 3 }}>
              <StepDrawer
                onItemClick={(item) => {
                  setDrawer(false)
                  selectedOppId = localStorage.getItem('oppId')
                  // For Malaysia, navigate to SSMVerification for UenDetails/PanDetail types
                  const getNavigationRoute = (type: Pages) => {
                    if (isMalaysiaHeader && (type === Pages.UenDetails || type === Pages.PanDetail)) {
                      return Pages.SSMVerification
                    }
                    return type
                  }
                  if (isEmpty(selectedOppId)) {
                    if (item.type === Pages.CreateLead || item.type === Pages.PanDetail || item.type === Pages.UenDetails) {
                      setTimeout(() => {
                        navigate(getRoutePath(getNavigationRoute(item.type)))
                      }, 100)
                    }
                  } else {
                    if (item.editableConfig?.editable) {
                      setTimeout(() => {
                        navigate(getRoutePath(getNavigationRoute(item.type)))
                      }, 100)
                    }
                  }
                }}
              />
            </Box>
          </Drawer>
          <Drawer
            variant={'permanent'}
            ModalProps={{ keepMounted: true }}
            sx={{
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: isMalaysiaHeader ? { xs: 'min(360px, 100vw)', md: '300px' } : '300px',
                background: { xs: '#fff', md: '#fdfdfd' },
                mt: {
                  xs: '0px',
                  md: '65px',
                },
              },
              display: {
                xs: 'none',
                md: isMalaysiaHeader ? (desktopDrawerOpen ? 'block' : 'none') : 'block',
              },
            }}
            anchor={'left'}
            open={isMalaysiaHeader ? desktopDrawerOpen : openDrawer}
            onClose={() => {
              if (isMalaysiaHeader) {
                setDesktopDrawerOpen(false)
                return
              }
              setDrawer(false)
            }}
          >
            <Box sx={{ overflowY: 'auto', height: '100%', pb: 6 }}>
              <StepDrawer
                onItemClick={(item) => {
                  setDrawer(false)
                  selectedOppId = localStorage.getItem('oppId')
                  // For Malaysia, navigate to SSMVerification for UenDetails/PanDetail types
                  const getNavigationRoute = (type: Pages) => {
                    if (isMalaysiaHeader && (type === Pages.UenDetails || type === Pages.PanDetail)) {
                      return Pages.SSMVerification
                    }
                    return type
                  }
                  if (!selectedOppId) {
                    if (item.type === Pages.CreateLead || item.type === Pages.PanDetail || item.type === Pages.UenDetails) {
                      setTimeout(() => {
                        navigate(getRoutePath(getNavigationRoute(item.type)))
                      }, 100)
                    }
                    return
                  }
                  if (item.editableConfig?.editable) {
                    setTimeout(() => {
                      navigate(getRoutePath(getNavigationRoute(item.type)))
                    }, 100)
                  }
                }}
              />
            </Box>
          </Drawer>
        </Box>
      )}
    </Box>
  ) : null
}

export default Header
